import { useDeleteConfirmation, useServices, useToast } from '@/app/providers';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import { useI18n } from '@/shared/i18n';
import { isSectionAdmin, useAbility } from '@/modules/identity/application/ability';
import type { FlatPermission, PermissionAction, Role } from '@/shared/types';
import { Check, Edit2, Lightbulb, Lock, LayoutDashboard, Plus, Save, Shield, Trash2, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { matchesSearch } from '@yawi/domain';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';

const USER_BACKEND_SUBJECT = 'User';
const USER_MENU_SUBJECTS = ['ResidentMenu', 'StaffMenu'] as const;
const ROLE_MENU_SUBJECT = 'RoleMenu';
const ROLE_BACKEND_SUBJECT = 'Role';

// Nombres reservados para roles/tipos de sistema. Un rol custom con uno de estos nombres
// podría confundirse (o, en código que compare por string en vez de por permiso real,
// hacerse pasar) por un rol privilegiado. Debe reflejar la blacklist del backend
// (CreateTenantRoleDto).
const RESERVED_ROLE_NAMES = new Set([
  'SUPER_ADMIN', 'TENANT_ADMIN', 'ADMIN', 'RESIDENT', 'GUARD', 'FAMILIAR', 'STAFF', 'ROOT', 'OWNER',
]);

// El backend exige que `Role.name` sea un código: MAYÚSCULAS, números, "-" y "_"
// únicamente (sin espacios ni acentos) — lo usa como identificador para permisos
// y para el `tenantRole` que se le asigna a cada usuario. Se genera automáticamente
// a partir de lo que escribe el admin, así puede seguir escribiendo nombres
// normales ("Jefe de Vigilancia") sin pensar en el formato interno del código.
const slugifyRoleName = (value: string): string =>
  value
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '') // quita acentos (á, é, í, ó, ú, ñ -> a, e, i, o, u, n)
    .toUpperCase()
    .replace(/[^A-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Permission groups for UI. Residentes/Personal are frontend-only menu subjects;
// they are expanded to User when saving so current backend policies keep working.
const PERMISSION_GROUPS = [
  {
    name: 'Comunidad',
    permissions: [
      { subject: 'ResidentMenu', label: 'Residentes' },
      { subject: 'Unit', label: 'Unidades' },
      { subject: 'UnitAllowedDevice', label: 'Puertas de unidad (asignar)', actions: ['read', 'update', 'delete'] as PermissionAction[] },
      { subject: 'Reservation', label: 'Reservaciones' },
      { subject: 'Amenity', label: 'Registro de amenidades' },
    ]
  },
  {
    name: 'Accesos',
    permissions: [
      { subject: 'AccessControl', label: 'Actividad de acceso' },
      { subject: 'AccessLog', label: 'Registro de visitas' },
      { subject: 'Ticket', label: 'Incidentes' },
      { subject: 'Shift', label: 'Turnos' },
    ]
  },
  {
    name: 'Comunicación',
    permissions: [
      { subject: 'Announcement', label: 'Comunicados' },
      { subject: 'Poll', label: 'Encuestas' },
      { subject: 'Notification', label: 'Notificaciones' },
    ]
  },
  {
    name: 'Finanzas',
    permissions: [
      { subject: 'Charge', label: 'Cuotas' },
      { subject: 'Payment', label: 'Pagos' },
      { subject: 'Expense', label: 'Gastos' },
      { subject: 'Billing', label: 'Facturación' },
      { subject: 'Invoice', label: 'Facturas' },
      { subject: 'BillingPlan', label: 'Planes de facturación' },
      { subject: 'FiscalData', label: 'Datos fiscales' },
    ]
  },
  {
    name: 'Operaciones',
    permissions: [
      { subject: 'StaffMenu', label: 'Personal' },
      { subject: 'RoleMenu', label: 'Roles' },
      { subject: 'Impersonation', label: 'Ver como (vista de residentes/personal)' },
      { subject: 'Supplier', label: 'Proveedores' },
      { subject: 'SupplierEmployee', label: 'Empleados de proveedor' },
      { subject: 'SupplierVisit', label: 'Visitas de proveedor' },
      { subject: 'Routine', label: 'Rutinas' },
      { subject: 'RoutineExecution', label: 'Ejecuciones de rutinas' },
    ]
  },
  {
    name: 'Configuración',
    permissions: [
      { subject: 'AccessDevice', label: 'Puntos de acceso' },
      { subject: 'RfidCredential', label: 'Tarjetas RFID' },
      { subject: 'NotificationConfig', label: 'Reglas de notificación' },
      { subject: 'Settings', label: 'Configuración general' },
      { subject: 'Integration', label: 'Integraciones' },
    ]
  },
];

const UI_PERMISSION_SUBJECTS = new Set(
  PERMISSION_GROUPS.flatMap(group => group.permissions.map(permission => permission.subject)),
);

const PERMISSION_LABELS = new Map(
  PERMISSION_GROUPS.flatMap(group => group.permissions.map(permission => [permission.subject, permission.label])),
);

const ACTIONS: { value: PermissionAction; label: string }[] = [
  { value: 'read', label: 'Ver' },
  { value: 'create', label: 'Crear' },
  { value: 'update', label: 'Editar' },
  { value: 'delete', label: 'Eliminar' },
];

const ACTION_LABELS = new Map(ACTIONS.map(action => [action.value, action.label]));

// Permisos que solo funcionan en la práctica si otro permiso relacionado también está activo
// (ej: la vista de Unidades necesita leer los puntos de acceso para asignar dispositivos).
type PermissionRecommendation = {
  subject: string;
  action: PermissionAction;
  suggestSubject: string;
  suggestAction: PermissionAction;
  reason: string;
};

const PERMISSION_RECOMMENDATIONS: PermissionRecommendation[] = [
  {
    subject: 'Unit',
    action: 'update',
    suggestSubject: 'UnitAllowedDevice',
    suggestAction: 'read',
    reason: 'Para ver y asignar puertas desde Unidades, este rol también necesita el permiso "Puertas de unidad".',
  },
  {
    subject: 'StaffMenu',
    action: 'read',
    suggestSubject: 'RoleMenu',
    suggestAction: 'read',
    reason: 'Si este rol administra Personal, te recomendamos agregar también Roles para poder ver los roles asignados a cada miembro.',
  },
];

// Permisos que el panel necesita SIEMPRE para el login y la primera carga (notificaciones,
// comunicados del dashboard, configuración/marca), sin importar qué módulos de negocio
// tenga asignados el rol. Si faltan, el usuario ve 403 apenas inicia sesión.
type BaselinePermission = {
  subject: string;
  action: PermissionAction;
  reason: string;
};

const LOGIN_BASELINE_PERMISSIONS: BaselinePermission[] = [
  {
    subject: 'Notification',
    action: 'read',
    reason: 'El panel carga las notificaciones y el contador de no leídas en cada pantalla.',
  },
  {
    subject: 'Announcement',
    action: 'read',
    reason: 'El Dashboard muestra los comunicados activos al iniciar sesión.',
  },
  {
    subject: 'Settings',
    action: 'read',
    reason: 'El panel carga la configuración general.',
  },
  {
    subject: 'FiscalData',
    action: 'read',
    reason: 'El panel también carga los datos fiscales del tenant al iniciar sesión.',
  },
];

// Secciones del Dashboard (Inicio) y qué permiso(s) de lectura necesita el rol para verlas.
// Debe reflejar la misma lógica de `permissions` en useDashboardData.ts / Dashboard.tsx.
// `requiredSubjects` se muestra entre paréntesis en la vista previa (ver PERMISSION_LABELS).
type DashboardSection = {
  key: string;
  label: string;
  requiredSubjects: string[];
};

const DASHBOARD_SECTIONS: DashboardSection[] = [
  { key: 'insight', label: 'Insight principal', requiredSubjects: ['Charge'] },
  { key: 'alertas', label: 'Alertas críticas', requiredSubjects: ['Charge', 'Ticket', 'AccessLog', 'Poll'] },
  { key: 'kpi-unidades', label: 'Unidades', requiredSubjects: ['Unit'] },
  { key: 'kpi-cobranza', label: 'Cobranza y mora', requiredSubjects: ['Charge'] },
  { key: 'kpi-accesos', label: 'Accesos de hoy', requiredSubjects: ['AccessLog'] },
  { key: 'finanzas', label: 'Finanzas', requiredSubjects: ['Charge'] },
  { key: 'accesos-dia', label: 'Accesos del día', requiredSubjects: ['AccessLog'] },
  { key: 'comunidad', label: 'Comunidad', requiredSubjects: ['Unit', 'Reservation', 'Announcement'] },
];


export const AdminRoleList = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { roleRepo, currentUser } = useServices();
  const ability = useAbility();

  // Un admin de sección crea roles de SU sección y solo delega permisos que él
  // mismo tiene; nunca usuarios, roles ni configuración (el backend lo impone,
  // aquí solo se ocultan para no ofrecer lo que va a rechazar).
  const actsAsSectionAdmin = isSectionAdmin(currentUser);
  const visiblePermissionGroups = useMemo(() => {
    if (!actsAsSectionAdmin) return PERMISSION_GROUPS;
    const notDelegable = new Set(['ResidentMenu', 'StaffMenu', 'RoleMenu', 'Impersonation', 'Settings', 'Integration']);
    return PERMISSION_GROUPS
      .map((group) => ({
        ...group,
        permissions: group.permissions
          .filter((perm) => !notDelegable.has(perm.subject))
          .map((perm) => ({
            ...perm,
            actions: (perm.actions ?? ACTIONS.map((a) => a.value)).filter((action) => ability.can(action, perm.subject)),
          }))
          .filter((perm) => perm.actions.length > 0),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [actsAsSectionAdmin, PERMISSION_GROUPS, ACTIONS, ability]);
  const canUseRoleAction = (action: 'create' | 'update' | 'delete') =>
    ability.can('manage', 'all') || ability.can(action, ROLE_MENU_SUBJECT);
  const canCreateRole = canUseRoleAction('create');
  const canUpdateRole = canUseRoleAction('update');
  const canDeleteRole = canUseRoleAction('delete');

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Partial<Role> | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Map<string, Set<PermissionAction>>>(new Map());

  const pendingRecommendations = useMemo(
    () => PERMISSION_RECOMMENDATIONS.filter((rec) => (
      selectedPermissions.get(rec.subject)?.has(rec.action)
      && !selectedPermissions.get(rec.suggestSubject)?.has(rec.suggestAction)
    )),
    [selectedPermissions],
  );

  const missingBaselinePermissions = useMemo(
    () => LOGIN_BASELINE_PERMISSIONS.filter((perm) => (
      !selectedPermissions.get(perm.subject)?.has(perm.action)
    )),
    [selectedPermissions],
  );

  const dashboardPreview = useMemo(() => {
    const hasRead = (subject: string) => selectedPermissions.get(subject)?.has('read') ?? false;
    return DASHBOARD_SECTIONS.map((section) => ({
      key: section.key,
      label: section.label,
      permissionHint: section.requiredSubjects.map((subject) => PERMISSION_LABELS.get(subject) ?? subject).join(' o '),
      visible: section.requiredSubjects.some(hasRead),
    }));
  }, [selectedPermissions]);

  const handleAddBaselinePermissions = () => {
    setSelectedPermissions((prev) => {
      const newMap = new Map(prev);
      missingBaselinePermissions.forEach(({ subject, action }) => {
        const subjectActions = new Set(newMap.get(subject));
        subjectActions.add(action);
        newMap.set(subject, subjectActions);
      });
      return newMap;
    });
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const data = await roleRepo.getRoles();
      setRoles(data);
    } catch {
      toast.error('Error al cargar los roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleOpenCreate = () => {
    setEditingRole({
      name: '',
      displayName: '',
      description: '',
      isSystem: false,
      permissions: [],
    });
    setSelectedPermissions(new Map());
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (role: Role) => {
    if (role.isSystem) {
      toast.error('Los roles del sistema no se pueden editar');
      return;
    }
    setEditingRole({ ...role });

    // Convert permissions to map for easier manipulation (handle both flat and grouped formats)
    const permMap = new Map<string, Set<PermissionAction>>();
    const backendUserActions = new Set<PermissionAction>();
    let hasExplicitUserMenuSubject = false;

    role.permissions.forEach(perm => {
      if (USER_MENU_SUBJECTS.includes(perm.subject as typeof USER_MENU_SUBJECTS[number])) {
        hasExplicitUserMenuSubject = true;
      }

      const expandAction = (action: PermissionAction, target: Set<PermissionAction>) => {
        if (action === 'manage') {
          ACTIONS.forEach(a => target.add(a.value));
        } else {
          target.add(action);
        }
      };

      if ('actions' in perm && Array.isArray((perm as any).actions)) {
        const expanded = new Set<PermissionAction>();
        ((perm as any).actions as PermissionAction[]).forEach(a => expandAction(a, expanded));
        if (perm.subject === USER_BACKEND_SUBJECT) {
          expanded.forEach(action => backendUserActions.add(action));
        } else if (UI_PERMISSION_SUBJECTS.has(perm.subject)) {
          permMap.set(perm.subject, expanded);
        }
      } else if ('action' in perm) {
        const action = (perm as any).action as PermissionAction;
        if (perm.subject === USER_BACKEND_SUBJECT) {
          expandAction(action, backendUserActions);
        } else if (UI_PERMISSION_SUBJECTS.has(perm.subject)) {
          const existing = permMap.get(perm.subject) || new Set<PermissionAction>();
          expandAction(action, existing);
          permMap.set(perm.subject, existing);
        }
      }
    });

    if (!hasExplicitUserMenuSubject && backendUserActions.size > 0) {
      USER_MENU_SUBJECTS.forEach(subject => {
        permMap.set(subject, new Set(backendUserActions));
      });
    }

    setSelectedPermissions(permMap);

    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (role: Role) => {
    if (role.isSystem) {
      toast.error('Los roles del sistema no se pueden eliminar');
      return;
    }

    await confirmDelete({
      itemName: role.displayName || role.name,
      itemType: 'Rol',
      onConfirm: async () => {
        await roleRepo.deleteRole(role.id);
        toast.success(`Rol "${role.displayName || role.name}" eliminado`);
        await fetchRoles();
      }
    });
  };

  const handleTogglePermission = (subject: string, action: PermissionAction) => {
    const newMap = new Map(selectedPermissions);
    const subjectActions = new Set(newMap.get(subject));

    if (subjectActions.has(action)) {
      subjectActions.delete(action);
    } else {
      subjectActions.add(action);
    }

    if (subjectActions.size === 0) {
      newMap.delete(subject);
    } else {
      newMap.set(subject, subjectActions);
    }

    setSelectedPermissions(newMap);
  };

  const handleToggleAllForSubject = (subject: string, availableActions: PermissionAction[] = ACTIONS.map(a => a.value)) => {
    const newMap = new Map(selectedPermissions);
    const subjectActions = newMap.get(subject);

    if (subjectActions && subjectActions.size === availableActions.length) {
      // Remove all
      newMap.delete(subject);
    } else {
      // Add all
      newMap.set(subject, new Set(availableActions));
    }

    setSelectedPermissions(newMap);
  };

  const handleSave = async () => {
    const displayName = editingRole?.displayName?.trim();
    if (!displayName) {
      toast.error('El nombre del rol es requerido');
      return;
    }

    // El único campo que llena el admin es el nombre visible; el código
    // interno que exige el backend (MAYÚSCULAS, sin espacios ni acentos) se
    // genera solo a partir de ese texto.
    const nameCode = slugifyRoleName(displayName);

    if (!nameCode) {
      toast.error('El nombre del rol es requerido');
      return;
    }

    if (RESERVED_ROLE_NAMES.has(nameCode)) {
      toast.error(`"${displayName}" es un nombre reservado del sistema. Elige otro nombre para el rol.`);
      return;
    }

    // Convert map to flat permissions array and add backend User permission for user menus.
    const flatPermissions: FlatPermission[] = [];
    const permissionKeys = new Set<string>();
    const addPermission = (action: PermissionAction, subject: string) => {
      const key = `${action}:${subject}`;
      if (permissionKeys.has(key)) return;
      permissionKeys.add(key);
      flatPermissions.push({ action, subject });
    };

    selectedPermissions.forEach((actions, subject) => {
      actions.forEach(action => {
        addPermission(action, subject);
        if (USER_MENU_SUBJECTS.includes(subject as typeof USER_MENU_SUBJECTS[number])) {
          addPermission(action, USER_BACKEND_SUBJECT);
        }
        if (subject === 'StaffMenu' && action === 'read') {
          addPermission('read', ROLE_BACKEND_SUBJECT);
        }
        if (subject === ROLE_MENU_SUBJECT) {
          addPermission(action, ROLE_BACKEND_SUBJECT);
        }
        // Por ahora, poder editar residentes implica poder asignar/transferir
        // el residente principal de una unidad — el backend valida esa acción
        // por separado con @CheckPolicies(Action.MANAGE, 'UnitPrincipal').
        if (subject === 'ResidentMenu' && action === 'update') {
          addPermission('manage', 'UnitPrincipal');
        }
      });
    });

    if (flatPermissions.length === 0) {
      toast.error('Debes seleccionar al menos un permiso');
      return;
    }

    try {
      if (isEditMode && editingRole.id) {
        await roleRepo.updateRole(editingRole.id, {
          name: nameCode,
          displayName,
          description: editingRole.description,
          permissions: flatPermissions,
        });
        toast.success(`Rol "${displayName}" actualizado`);
      } else {
        await roleRepo.createRole({
          name: nameCode,
          displayName,
          description: editingRole.description,
          permissions: flatPermissions,
        });
        toast.success(`Rol "${displayName}" creado`);
      }
      setIsModalOpen(false);
      await fetchRoles();
    } catch {
      toast.error('Error al guardar el rol');
    }
  };

  const getPermissionCount = (role: Role): number => {
    if (role.permissions.some(p => p.subject === 'all')) {
      return PERMISSION_GROUPS.flatMap(g => g.permissions).length * ACTIONS.length;
    }
    return role.permissions.reduce((acc, p) => {
      if (!UI_PERMISSION_SUBJECTS.has(p.subject)) {
        return acc;
      }
      if ('actions' in p && Array.isArray((p as any).actions)) {
        return acc + (p as any).actions.length;
      }
      return acc + 1;
    }, 0);
  };

  const columns: ColumnDef<Role>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Nombre',
      cell: (role) => (
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${role.isSystem ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
            {role.isSystem ? <Lock size={14} /> : <Shield size={14} />}
          </div>
          <div>
            <p className="font-medium text-slate-900">{role.displayName || role.name}</p>
            {role.description && (
              <p className="text-xs text-slate-500">{role.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Tipo',
      cell: (role) => (
        role.isSystem ? (
          <span className="inline-flex items-center gap-1 text-purple-700 text-xs font-bold bg-purple-50 px-2 py-1 rounded-full border border-purple-200">
            <Lock size={10} /> Sistema
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-blue-700 text-xs font-bold bg-blue-50 px-2 py-1 rounded-full border border-blue-200">
            <Shield size={10} /> Personalizado
          </span>
        )
      ),
    },
    {
      id: 'permissions',
      header: 'Permisos',
      cell: (role) => (
        <span className="text-sm text-slate-600">
          {getPermissionCount(role)} {getPermissionCount(role) === 1 ? 'permiso' : 'permisos'}
        </span>
      ),
    },
  ], []);

  return (
    <>
      <DataTable
        data={roles.filter((r) => matchesSearch(search, r.displayName, r.name))}
        columns={columns}
        keyExtractor={(role) => role.id}
        meta={null}
        onPageChange={() => {}}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar rol..."
        title={t('admin.card.roles')}
        loading={loading}
        emptyMessage="No hay roles configurados."
        actions={
          canCreateRole ? (
            <Button onClick={handleOpenCreate} icon={<Plus size={16}/>}>
              {t('admin.roles.btn.create')}
            </Button>
          ) : undefined
        }
        rowActions={(role) => (
          <>
            {canUpdateRole && (
              <button
                onClick={() => handleOpenEdit(role)}
                className={`p-1.5 rounded transition-colors ${role.isSystem ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:text-primary-600 hover:bg-primary-50'}`}
                title={role.isSystem ? 'Rol del sistema (no editable)' : t('common.edit')}
                disabled={role.isSystem}
              >
                <Edit2 size={16} />
              </button>
            )}
            {canDeleteRole && (
              <button
                onClick={() => handleDelete(role)}
                className={`p-1.5 rounded transition-colors ${role.isSystem ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:text-red-600 hover:bg-red-50'}`}
                title={role.isSystem ? 'Rol del sistema (no eliminable)' : t('common.delete')}
                disabled={role.isSystem}
              >
                <Trash2 size={16} />
              </button>
            )}
          </>
        )}
      />

      {/* Create/Edit Modal */}
      {isModalOpen && editingRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">
                {isEditMode ? 'Editar Rol' : 'Nuevo Rol'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20}/>
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Input
                    label="Nombre del Rol"
                    value={editingRole.displayName || ''}
                    onChange={e => setEditingRole({...editingRole, displayName: e.target.value})}
                    placeholder="Ej: Jefe de Vigilancia, Tesorero de la comunidad..."
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    label="Descripción"
                    value={editingRole.description || ''}
                    onChange={e => setEditingRole({...editingRole, description: e.target.value})}
                    placeholder="Breve descripción del rol"
                  />
                </div>
              </div>

              {/* Permissions */}
              <div>
                <h4 className="font-bold text-slate-800 mb-4">Permisos</h4>

                <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <div className="flex items-start gap-2">
                    <LayoutDashboard size={14} className="shrink-0 mt-0.5 text-indigo-600" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-indigo-800 mb-2">
                        Vista previa del Dashboard (Inicio) para este rol
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {dashboardPreview.map((section) => (
                          <div key={section.key} className="flex items-center gap-1.5 text-xs">
                            {section.visible ? (
                              <Check size={12} className="shrink-0 text-green-600" />
                            ) : (
                              <Lock size={12} className="shrink-0 text-slate-400" />
                            )}
                            <span className={section.visible ? 'text-indigo-900' : 'text-slate-400'}>
                              {section.label} <span className="opacity-70">({section.permissionHint})</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {missingBaselinePermissions.length > 0 && (
                  <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                      <Lightbulb size={14} className="shrink-0 mt-0.5 text-amber-600" />
                      <div className="flex-1 space-y-1.5">
                        <p className="text-xs font-semibold text-amber-800">
                          Permisos base recomendados para que este rol pueda iniciar sesión sin errores
                        </p>
                        <ul className="space-y-1">
                          {missingBaselinePermissions.map((perm) => (
                            <li key={`${perm.subject}-${perm.action}`} className="text-xs text-amber-800">
                              <span className="font-medium">{PERMISSION_LABELS.get(perm.subject)}:</span> {perm.reason}
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          onClick={handleAddBaselinePermissions}
                          className="text-xs font-semibold text-amber-900 underline hover:no-underline"
                        >
                          Agregar todos los permisos base
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {visiblePermissionGroups.map((group) => (
                    <div key={group.name} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <h5 className="font-semibold text-slate-700 mb-3">{group.name}</h5>

                      {/* Header row */}
                      <div className="grid grid-cols-6 gap-2 mb-2 text-xs font-medium text-slate-500">
                        <div className="col-span-2">Módulo</div>
                        {ACTIONS.map(action => (
                          <div key={action.value} className="text-center">{action.label}</div>
                        ))}
                      </div>

                      {/* Permission rows */}
                      {group.permissions.map((perm) => {
                        const availableActions = perm.actions ?? ACTIONS.map(a => a.value);
                        const subjectActions = selectedPermissions.get(perm.subject) || new Set();
                        const hasAll = subjectActions.size === availableActions.length && availableActions.length > 0;
                        const rowRecommendations = pendingRecommendations.filter((rec) => rec.subject === perm.subject);
                        return (
                          <Fragment key={perm.subject}>
                            <div className="grid grid-cols-6 gap-2 py-2 border-t border-slate-200 items-center">
                              <div className="col-span-2 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleToggleAllForSubject(perm.subject, availableActions)}
                                  className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                    hasAll
                                      ? 'bg-primary-600 border-primary-600 text-white'
                                      : subjectActions.size > 0
                                      ? 'bg-primary-100 border-primary-400'
                                      : 'border-slate-300 hover:border-primary-400'
                                  }`}
                                  title="Seleccionar todos"
                                >
                                  {hasAll && <Check size={10} />}
                                </button>
                                <span className="text-sm text-slate-700">{perm.label}</span>
                              </div>
                              {ACTIONS.map(action => (
                                <div key={action.value} className="flex justify-center">
                                  {availableActions.includes(action.value) && (
                                    <button
                                      type="button"
                                      onClick={() => handleTogglePermission(perm.subject, action.value)}
                                      className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${
                                        subjectActions.has(action.value)
                                          ? 'bg-primary-600 border-primary-600 text-white'
                                          : 'border-slate-300 hover:border-primary-400 hover:bg-primary-50'
                                      }`}
                                    >
                                      {subjectActions.has(action.value) && <Check size={12} />}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                            {rowRecommendations.map((rec) => (
                              <div
                                key={`${rec.subject}-${rec.suggestSubject}-${rec.suggestAction}`}
                                className="flex items-center gap-2 -mt-1 mb-1 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800"
                              >
                                <Lightbulb size={12} className="shrink-0" />
                                <span className="flex-1">{rec.reason}</span>
                                <button
                                  type="button"
                                  onClick={() => handleTogglePermission(rec.suggestSubject, rec.suggestAction)}
                                  className="font-semibold underline hover:no-underline shrink-0"
                                >
                                  Agregar {ACTION_LABELS.get(rec.suggestAction)} en {PERMISSION_LABELS.get(rec.suggestSubject)}
                                </button>
                              </div>
                            ))}
                          </Fragment>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-slate-100">
              <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                {t('common.close')}
              </Button>
              <Button type="button" variant="primary" className="flex-1" icon={<Save size={16}/>} onClick={handleSave}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
